import styled, { keyframes } from "styled-components";

// Animations
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeOutDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(30px);
  }
`;

export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components
export const PageHeader = styled.section`
  background: var(--surface);
  padding-top: 20px;
  margin-top: 75px;
  text-align: center;
  padding-bottom: 30px;

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }

  @media (max-width: 920px) {
    margin-top: 66.7px;
  }
`;

export const FiltersSection = styled.section`
  background: var(--surface);
  padding: 20px;
  border-bottom: 1px solid var(--accent-light);
`;

export const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

export const FilterButton = styled.button`
  background: ${(props) => (props.active ? "var(--accent)" : "transparent")};
  border: 2px solid
    ${(props) => (props.active ? "var(--accent)" : "var(--accent-light)")};
  color: ${(props) =>
    props.active ? "var(--surface)" : "var(--text-secondary)"};
  padding: 10px 20px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--surface);
  }
`;

export const CourseTags = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const CourseTag = styled.span`
  background: var(--surface);
  color: var(--accent);
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 11px;
  text-transform: uppercase;
  border: 1px solid var(--accent-light);
`;

export const CourseRating = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

export const Stars = styled.div`
  display: flex;
  gap: 2px;
  color: var(--warning);
`;

export const RatingText = styled.span`
  color: var(--text-muted);
  font-size: 14px;
`;

export const CourseActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 25px;
`;

export const AddToCartButton = styled.button`
  flex: 1;
  background: ${(props) =>
    props.added ? "var(--success)" : "var(--gradient-primary)"};
  color: var(--surface);
  padding: 15px;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  font-family: inherit;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;

  &:hover {
    background: ${(props) =>
      props.added ? "var(--success)" : "var(--accent-dark)"};
    color: var(--surface);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }
`;

export const WishlistButton = styled.button`
  background: ${(props) => (props.active ? "var(--accent)" : "transparent")};
  border: 2px solid
    ${(props) => (props.active ? "var(--accent)" : "var(--accent-light)")};
  color: ${(props) =>
    props.active ? "var(--surface)" : "var(--text-secondary)"};
  padding: 15px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${(props) => (props.justToggled ? bounceIn : "none")} 0.6s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: ${(props) => (props.active ? "var(--accent)" : "transparent")};
  }
`;

export const CartIcon = styled.div`
  position: relative;
  color: var(--text-secondary);
  font-size: 24px;
  margin-left: 20px;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent);
  }
`;

export const CartCount = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--accent);
  color: var(--surface);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  animation: ${(props) => (props.updated ? pulse : "none")} 0.3s ease;
`;

export const CTAButton = styled.a`
  background: var(--gradient-primary);
  color: var(--text-primary);
  padding: 15px 35px;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  box-shadow: var(--shadow-soft);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

export const NoResults = styled.div`
  text-align: center;
  padding: 80px 40px;
  color: var(--text-muted);
  animation: ${fadeInUp} 0.6s ease forwards;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h3 {
    font-size: 24px;
    margin-bottom: 15px;
    color: var(--text-secondary);
  }

  p {
    margin-bottom: 30px;
    line-height: 1.6;
  }
`;

// Container for the main content
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

// Success message for notifications
export const SuccessMessage = styled.div`
  background: var(--success);
  color: var(--surface);
  padding: 15px 20px;
  border-radius: 25px;
  margin-bottom: 20px;
  display: ${(props) => (props.show ? "flex" : "none")};
  align-items: center;
  gap: 10px;
  font-size: 14px;
  animation: ${(props) => (props.show ? fadeInUp : fadeOutDown)} 0.3s ease
    forwards;
`;

// Error message styling
export const ErrorMessage = styled.div`
  background: #f5a5a5;
  color: var(--surface);
  padding: 15px 20px;
  border-radius: 25px;
  margin-bottom: 20px;
  display: ${(props) => (props.show ? "flex" : "none")};
  align-items: center;
  gap: 10px;
  font-size: 14px;
  animation: ${(props) => (props.show ? fadeInUp : fadeOutDown)} 0.3s ease
    forwards;
`;

// Badge component for course tags
export const Badge = styled.span`
  background: ${(props) => {
    switch (props.variant) {
      case "success":
        return "var(--success)";
      case "warning":
        return "var(--warning)";
      case "primary":
        return "var(--accent)";
      default:
        return "var(--secondary)";
    }
  }};
  color: ${(props) =>
    props.variant && props.variant !== "secondary"
      ? "var(--surface)"
      : "var(--text-secondary)"};
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Tooltip component
export const Tooltip = styled.div`
  position: relative;
  display: inline-block;

  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background: var(--primary);
    color: var(--surface);
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    animation: ${fadeInUp} 0.2s ease;
  }

  &:hover::before {
    content: "";
    position: absolute;
    bottom: 115%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: var(--primary);
    z-index: 1000;
  }
`;

// Modal overlay
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${(props) => (props.show ? fadeInUp : fadeOutDown)} 0.3s ease
    forwards;
`;

// Modal content
export const ModalContent = styled.div`
  background: var(--surface);
  border-radius: 25px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-medium);
  animation: ${scaleIn} 0.3s ease forwards;
`;

// Skeleton loader for course cards
export const SkeletonCard = styled.div`
  background: var(--surface);
  border-radius: 25px;
  padding: 45px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);

  .skeleton-image {
    width: 100%;
    height: 200px;
    background: linear-gradient(
      90deg,
      var(--secondary) 25%,
      var(--accent-light) 50%,
      var(--secondary) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
    border-radius: 15px;
    margin-bottom: 25px;
  }

  .skeleton-text {
    height: 20px;
    background: linear-gradient(
      90deg,
      var(--secondary) 25%,
      var(--accent-light) 50%,
      var(--secondary) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
    border-radius: 10px;
    margin-bottom: 15px;
  }

  .skeleton-text.short {
    width: 60%;
  }

  .skeleton-text.medium {
    width: 80%;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Floating action button
export const FloatingButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: var(--gradient-primary);
  border: none;
  border-radius: 50%;
  box-shadow: var(--shadow-medium);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--surface);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    transform: translateY(-5px) scale(1.1);
    box-shadow: 0 15px 45px rgba(232, 165, 165, 0.4);
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

// Search input styling
export const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 20px 12px 45px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  transition: border-color 0.3s ease;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%239d9d9d"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>');
  background-repeat: no-repeat;
  background-position: 15px center;
  background-size: 20px;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

// Price range slider
export const PriceRange = styled.div`
  margin: 20px 0;

  label {
    display: block;
    margin-bottom: 10px;
    font-size: 14px;
    color: var(--text-secondary);
    text-transform: uppercase;
    font-weight: 600;
  }

  input[type="range"] {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: var(--accent-light);
    outline: none;
    -webkit-appearance: none;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--accent);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.2);
      }
    }

    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--accent);
      cursor: pointer;
      border: none;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.2);
      }
    }
  }
`;

export const Section = styled.section`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

export const CoursesSection = styled(Section)`
  background: var(--background);
  /* Mini edukacije su sada prva sekcija, pa kursevi idu odmah ispod */
  padding-top: 0;
`;

export const CoursesGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  align-items: stretch;

  /* Od laptop širine naviše drži tačno 3 kartice u redu — na 13" laptopu
     je inače flex-basis (420px) bio preširok pa se lomilo na 2 pa 1.
     max-width: 420px zadržava izgled na velikim ekranima. */
  @media (min-width: 992px) {
    & > * {
      flex: 1 1 calc((100% - 4rem) / 3);
      max-width: 420px;
      min-width: 0;
    }
  }
`;

export const MiniSection = styled(Section)`
  background: var(--background);
`;

export const MiniDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 920px;
  margin: 0 auto 32px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--accent-light);
  }

  span {
    font-size: 18px;
    font-weight: 700;
    color: var(--primary);
    text-transform: uppercase;
    letter-spacing: 1px;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  small {
    background: var(--gradient-primary);
    color: var(--surface);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 10px;
    border-radius: 20px;
  }
`;

export const MiniGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  align-items: center;
`;

export const CourseContent = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const CourseBadge = styled.span`
  display: inline-block;
  align-self: flex-start;
  padding: 6px 16px;
  background: var(--gradient-primary);
  color: var(--surface);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: "Source Code Pro", monospace;
`;

export const CourseTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
  margin: 0;
`;

export const CourseStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  border-top: 1px solid var(--accent-light);
  border-bottom: 1px solid var(--accent-light);
`;

export const CourseStat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 14px;

  svg {
    color: var(--primary);
    font-size: 16px;
    min-width: 16px;
  }

  span {
    line-height: 1.4;
  }
`;

export const CourseCard = styled.div`
  background: var(--surface);
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  position: relative;
  transition: all 0.4s ease;
  border: 2px solid var(--accent-light);
  overflow: hidden;
  box-shadow: var(--shadow-soft);
  animation: ${(props) => (props.$animate ? scaleIn : "none")} 0.6s ease
    forwards;
  opacity: ${(props) => (props.$animate ? 1 : 0)};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-medium);
    border-color: var(--primary);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const CourseImage = styled.div`
  width: 100%;
  height: 240px;
  background-image: url("https://lukaya.art/wp-content/uploads/2025/07/Shotcut_00_00_25_833-1-scaled.jpg");
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(to top, var(--surface), transparent);
  }
`;

export const CourseDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 15px;
  margin: 0;
`;

export const CoursePrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 10px 0;

  .original-price {
    text-decoration: line-through;
    color: var(--text-muted);
    font-size: 18px;
    font-weight: 500;
  }

  .current-price {
    font-size: 36px;
    font-weight: 800;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-family: "Source Code Pro", monospace;
  }
`;

export const CourseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

export const CourseIcon = styled.div`
  width: 55px;
  height: 55px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--surface);
`;

export const CourseFeatures = styled.ul`
  list-style: none;
  margin: 25px 0;

  li {
    padding: 12px 0;
    padding-left: 35px;
    position: relative;
    color: var(--text-secondary);
    border-bottom: 1px solid var(--accent-light);

    &:last-child {
      border-bottom: none;
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="%23a8d8a8"/></svg>')
        no-repeat center;
      background-size: contain;
    }
  }
`;
